
import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Filter, FileSpreadsheet } from 'lucide-react';
import { Courier } from '@/services/types';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuLabel,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuSeparator,
} from '@/components/ui/dropdown-menu';
import { toast } from '@/components/ui/sonner';

interface CourierFiltersProps {
  setStatusFilter: (status: string | null) => void;
  couriers: Courier[];
}

export const CourierFilters: React.FC<CourierFiltersProps> = ({ setStatusFilter, couriers }) => {
  const [currentFilter, setCurrentFilter] = useState<string | null>(null);

  const handleFilterChange = (value: string) => {
    if (value === 'all') {
      setStatusFilter(null);
      setCurrentFilter(null);
    } else {
      setStatusFilter(value);
      setCurrentFilter(value);
    }
  };

  const handleExportToExcel = () => {
    try {
      const csvContent = [
        ['ID', 'Имя', 'Email', 'Телефон', 'Статус', 'Тариф', 'Номер сделки', 'Дата регистрации', 'Telegram', 'Avito', 'VK'].join(','),
        ...couriers.map(courier => [
          courier.id,
          courier.name,
          courier.email || '',
          courier.phone || '',
          courier.status === 'active' ? 'Активен' : 'Неактивен',
          courier.tariff || '',
          courier.deal_number || '',
          new Date(courier.registeredAt).toLocaleDateString('ru-RU'),
          courier.telegram_id || '',
          courier.avito_id || '',
          courier.vk_id || ''
        ].join(','))
      ].join('\n');
      
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `Клиенты_${new Date().toISOString().slice(0, 10)}.csv`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      toast.success('Экспорт выполнен успешно');
    } catch (error) {
      console.error('Ошибка при экспорте:', error);
      toast.error('Не удалось экспортировать данные');
    }
  };

  const getFilterLabel = (filter: string | null) => {
    if (!filter) return 'Фильтры';
    
    const labels: Record<string, string> = {
      active: 'Активные',
      inactive: 'Неактивные',
      telegram: 'Telegram',
      avito: 'Авито',
      phone: 'Только телефон',
      paid: 'Своевременные оплаты',
      delayed: 'Задержки оплат',
      'multiple-payments': 'Больше 1 оплаты'
    };
    
    return `Фильтр: ${labels[filter] || filter}`;
  };

  return (
    <div className="flex items-center justify-end">
      <div className="flex space-x-2">
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="outline" size="sm">
              <Filter className="mr-2 h-4 w-4" />
              {getFilterLabel(currentFilter)}
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-56">
            <DropdownMenuLabel>Статус клиента</DropdownMenuLabel>
            <DropdownMenuRadioGroup value={currentFilter || 'all'} onValueChange={handleFilterChange}>
              <DropdownMenuRadioItem value="all">Все статусы</DropdownMenuRadioItem>
              <DropdownMenuRadioItem value="active">Активные</DropdownMenuRadioItem>
              <DropdownMenuRadioItem value="inactive">Неактивные</DropdownMenuRadioItem>
            </DropdownMenuRadioGroup>
            
            <DropdownMenuSeparator />
            
            <DropdownMenuLabel>Каналы связи</DropdownMenuLabel>
            <DropdownMenuRadioGroup value={currentFilter || 'all'} onValueChange={handleFilterChange}>
              <DropdownMenuRadioItem value="telegram">Telegram</DropdownMenuRadioItem>
              <DropdownMenuRadioItem value="avito">Авито</DropdownMenuRadioItem>
              <DropdownMenuRadioItem value="phone">Только телефон</DropdownMenuRadioItem>
            </DropdownMenuRadioGroup>
            
            <DropdownMenuSeparator />
            
            <DropdownMenuLabel>Оплаты</DropdownMenuLabel>
            <DropdownMenuRadioGroup value={currentFilter || 'all'} onValueChange={handleFilterChange}>
              <DropdownMenuRadioItem value="multiple-payments">Больше 1 оплаты</DropdownMenuRadioItem>
              <DropdownMenuRadioItem value="paid">Своевременные оплаты</DropdownMenuRadioItem>
              <DropdownMenuRadioItem value="delayed">Задержки оплат</DropdownMenuRadioItem>
            </DropdownMenuRadioGroup>
          </DropdownMenuContent>
        </DropdownMenu>

        <Button variant="outline" size="sm" onClick={handleExportToExcel}>
          <FileSpreadsheet className="mr-2 h-4 w-4" />
          Экспорт
        </Button>
      </div>
    </div>
  );
};
